import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Grid, Card, Box } from '@mui/material';
import {
  LineChart as ReLineChart,
  Line as ReLine,
  XAxis as ReXAxis,
  YAxis as ReYAxis,
  CartesianGrid as ReCartesianGrid,
  Tooltip as ReTooltip,
  Legend as ReLegend,
  BarChart as ReBarChart,
  Bar as ReBar,
  PieChart as RePieChart,
  Pie as RePie,
  Cell as ReCell,
  ResponsiveContainer as ReResponsiveContainer,
} from 'recharts';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { ptBR } from 'date-fns/locale';
import moment from 'moment';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import validator from 'assets/img/validator.svg';
import total_sales from 'assets/img/total_sales.svg';
import pizza from 'assets/img/pizza.svg';
import download from 'assets/img/download.svg';
import calendar from 'assets/img/calendar.svg';
import logo_clubs from 'assets/img/logo_clubs.svg';
import logo_sindjus from 'assets/img/logo_sindjus.svg';
import logo_sindilegis from 'assets/img/logo_sindilegis.svg';
import logo_astcu from 'assets/img/logo_astcu.svg';

import SummaryCard from './components/SummaryCard';
import PeriodSelector from './components/PeriodSelector';
import ProductSelectorCard from './components/ProductSelectorCard';
import PieChartCard from './components/PieChartCard';
import BarChartCard from './components/BarChartCard';
import StockPieChartCard from './components/StockPieChardCard';
import LineChartCard from './components/LineChartCard';

import {
  getList as getListDashboard,
  getListProductsOrders,
  getAllDashboardData,
} from './FinancialDashboardActions';
import { getUsersSubscriptionActionsList } from '../actions/subscribeProduct/subscribeProductActions';
import UsersTable from './components/UsersTable';
import { groupByDateAndKey, getSavedFilter } from './components/common/uteis';

import { styles } from './styles';
import LoadingProgress from 'common/components/progress/loading';
import { Typography, Tabs, Tab, TableContainer } from '@material-ui/core';
import { isEmpty } from 'lodash';
import Table from 'common/components/table/table-material';
import { isMobile } from 'react-device-detect';

const PAYMENT_LABEL = {
  credit_card: 'Cartão de crédito',
  pix: 'PIX',
  boleto: 'Boleto',
  manual: 'Manual',
};

const SMALL_CHART_WIDTH = 320;
const SMALL_CHART_HEIGHT = 180;
const LARGE_LINE_CHART_WIDTH = 860;
const LARGE_LINE_CHART_HEIGHT = 220;
const ACTIVITY_CHART_LIMIT = 8;
const ACTIVITY_LABEL_MAX_LENGTH = 28;
const LOGO_CARD_ITEM_STYLE = { flex: '0 0 120px', maxWidth: 120 };
const FILTER_CARD_ITEM_STYLE = { flex: '0 0 140px', maxWidth: 140 };
const SUMMARY_CARD_ITEM_STYLE = { flex: '0 0 130px', maxWidth: 130 };
const VALUE_CARD_ITEM_STYLE = { flex: '1 1 260px', minWidth: 260 };

const Dashboard = ({
  auth,
  vouchers,
  productOrders,
  getListDashboard,
  getListProductsOrders,
  getAllDashboardData,
  usersSubscriptions,
  getUsersSubscriptionActionsList,
  loading,
}) => {
  const stationInfo = auth?.user?.establishments?.[0]?.stations?.[0] || {};
  const station = stationInfo?.id;
  const stationName = stationInfo?.name || '';
  const [activeTab, setActiveTab] = useState('principal');
  const [recurrenceLoaded, setRecurrenceLoaded] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const prevLoadingRef = useRef(true);
  const [yearFilter, setYearFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [voucherStatusFilter, setVoucherStatusFilter] = useState('');
  const [recYearFilter, setRecYearFilter] = useState('');
  const [recMonthFilter, setRecMonthFilter] = useState('');
  const [recPartnerFilter, setRecPartnerFilter] = useState('');
  const PAGAMENTO_CONFIRMADO = 'Pagamento confirmado';
  const PERCENTUAL = 0.089;

  const [startDate, setStartDate] = useState(() => getSavedFilter('startDate', new Date()));
  const [endDate, setEndDate] = useState(() => getSavedFilter('endDate', new Date()));
  const [selectedProducts, setSelectedProducts] = useState(() =>
    getSavedFilter('selectedProducts', [])
  );

  const useFilteredVouchers = (vouchers, startDate, endDate) => {
    return useMemo(() => {
      return vouchers.filter((voucher) => {
        const voucherDate = new Date(voucher.createdAt).setHours(0, 0, 0, 0);
        const start = new Date(startDate).setHours(0, 0, 0, 0);
        const end = new Date(endDate).setHours(23, 59, 59, 999);
        return voucherDate >= start && voucherDate <= end;
      });
    }, [vouchers, startDate, endDate]);
  };
  const filteredSubscriptions = useFilteredVouchers(vouchers, startDate, endDate);
  const filteredProductOrders = useFilteredVouchers(productOrders, startDate, endDate);
  const allConfirmedProductOrders = useMemo(
    () => (productOrders || []).filter((order) => order.status === PAGAMENTO_CONFIRMADO),
    [productOrders]
  );
  const confirmedProductOrders = useMemo(
    () => filteredProductOrders.filter((order) => order.status === PAGAMENTO_CONFIRMADO),
    [filteredProductOrders]
  );
  const tableData = useMemo(() => {
    if (isEmpty(selectedProducts)) {
      return confirmedProductOrders;
    }

    return confirmedProductOrders.filter((order) =>
      selectedProducts.some((product) => product.id === order.product?.id)
    );
  }, [confirmedProductOrders, selectedProducts]);
  const [searchTerm, setSearchTerm] = useState('');

  const allSales = useMemo(
    () => [...allConfirmedProductOrders, ...(vouchers || [])],
    [allConfirmedProductOrders, vouchers]
  );

  const availableYears = useMemo(() => {
    const years = allSales
      .map((item) => moment(item.createdAt).year())
      .filter((year) => !!year && !Number.isNaN(year));
    return Array.from(new Set(years)).sort();
  }, [allSales]);

  const availableMonths = useMemo(() => {
    const months = allSales
      .map((item) => moment(item.createdAt).month() + 1)
      .filter((month) => !!month && !Number.isNaN(month));
    return Array.from(new Set(months)).sort();
  }, [allSales]);

  const availablePartners = useMemo(() => {
    const partners = new Set();
    allSales.forEach((item) => {
      const partnerName = getPartnerName(item);
      if (partnerName) {
        partners.add(partnerName);
      }
    });
    return Array.from(partners).sort();
  }, [allSales]);

  const recurrenceAvailableYears = useMemo(() => {
    const years = (usersSubscriptions || [])
      .map((item) => moment(item.subscription_date).year())
      .filter((year) => !!year && !Number.isNaN(year));
    return Array.from(new Set(years)).sort();
  }, [usersSubscriptions]);

  const recurrenceAvailableMonths = useMemo(() => {
    const months = (usersSubscriptions || [])
      .map((item) => moment(item.subscription_date).month() + 1)
      .filter((month) => !!month && !Number.isNaN(month));
    return Array.from(new Set(months)).sort();
  }, [usersSubscriptions]);

  const recurrenceAvailablePartners = useMemo(() => {
    const partners = new Set();
    (usersSubscriptions || []).forEach((item) => {
      if (item.partner) {
        partners.add(item.partner);
      }
    });
    return Array.from(partners).sort();
  }, [usersSubscriptions]);

  const isVoucherValidated = (value) =>
    value === true || value === 1 || value === '1' || value === 'Sim' || value === 'sim';

  function getPartnerName(item) {
    const partner =
      item?.action?.partner ||
      item?.presential_course?.action?.partner ||
      item?.presential_course?.partner ||
      null;

    if (!partner) return null;
    return typeof partner === 'string' ? partner : partner?.name || null;
  }

  function getEntityName(item) {
    return (
      item?.action?.station?.name ||
      item?.presential_course?.action?.station?.name ||
      (item?.saleSource === 'payment_link' ? stationName : null) ||
      'Outros'
    );
  }

  function formatActivityLabel(label) {
    if (!label) return 'Outras atividades';
    return label.length > ACTIVITY_LABEL_MAX_LENGTH
      ? `${label.slice(0, ACTIVITY_LABEL_MAX_LENGTH)}...`
      : label;
  }

  const getSaleGrossValue = (item) => {
    if (item?.product?.value != null) {
      const value = parseFloat(item.product.value) || 0;
      const discount = parseFloat(item.product.discount_value) || 0;
      return value - discount;
    }

    return parseFloat(item?.grossValue) || 0;
  };

  const getPaymentMethodLabel = (method) => {
    if (method === 'pix') return 'PIX';
    if (method === 'credit_card') return 'Cartão de crédito';
    if (method === 'boleto') return 'Boleto';
    return method;
  };

  const isVoucherApplicable = (item) => item?.voucherApplicable !== false;

  const getVoucherStatusLabel = (item) => {
    if (!isVoucherApplicable(item)) return 'Não se aplica';
    return isVoucherValidated(item.validated) ? 'Resgatado' : 'Aguardando';
  };

  const principalFilteredSales = useMemo(() => {
    return allSales.filter((item) => {
      const date = moment(item.createdAt);
      if (!date.isValid()) return false;

      const yearMatch = !yearFilter || date.year().toString() === yearFilter;
      const monthMatch = !monthFilter || (date.month() + 1).toString() === monthFilter.toString();

      const partnerName = getPartnerName(item);
      const partnerMatch = !partnerFilter || partnerName === partnerFilter;

      return yearMatch && monthMatch && partnerMatch;
    });
  }, [allSales, yearFilter, monthFilter, partnerFilter]);

  const principalSalesCount = principalFilteredSales.length;

  const principalTotalSold = useMemo(() => {
    return principalFilteredSales.reduce((sum, item) => {
      return sum + getSaleGrossValue(item);
    }, 0);
  }, [principalFilteredSales]);

  const recurrenceFilteredData = useMemo(() => {
    return (usersSubscriptions || []).filter((item) => {
      const date = moment(item.subscription_date);
      if (!date.isValid()) return false;

      const yearMatch = !recYearFilter || date.year().toString() === recYearFilter;
      const monthMatch =
        !recMonthFilter || (date.month() + 1).toString() === recMonthFilter.toString();

      const partnerName = item.partner;
      const partnerMatch = !recPartnerFilter || partnerName === recPartnerFilter;

      return yearMatch && monthMatch && partnerMatch;
    });
  }, [usersSubscriptions, recYearFilter, recMonthFilter, recPartnerFilter]);

  const recurrenceSalesCount = useMemo(() => {
    return recurrenceFilteredData.reduce((sum, item) => sum + (item.total_pago || 0), 0);
  }, [recurrenceFilteredData]);

  const recurrenceTotalSold = useMemo(() => {
    return recurrenceFilteredData.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      const paidCount = item.total_pago || 0;
      return sum + price * paidCount;
    }, 0);
  }, [recurrenceFilteredData]);

  const recurrenceServiceBarData = useMemo(() => {
    const counts = {};

    recurrenceFilteredData.forEach((item) => {
      const name = item.subscription_description || 'Outros serviços';
      const value = item.total_pago || 0;
      if (!name) return;
      counts[name] = (counts[name] || 0) + value;
    });

    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [recurrenceFilteredData]);

  const recurrenceEntityBarData = useMemo(() => {
    const counts = {};

    recurrenceFilteredData.forEach((item) => {
      const entityName = item.station || 'Outras entidades';
      const value = item.total_pago || 0;
      if (!entityName) return;
      counts[entityName] = (counts[entityName] || 0) + value;
    });

    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [recurrenceFilteredData]);

  const recurrenceTableRows = useMemo(() => {
    return recurrenceFilteredData
      .map((item) => {
        const grossValue = parseFloat(item.price) || 0;
        const netValue = parseFloat(item.price_minus_8_3_percent) || 0;
        const feeValue = Math.max(grossValue - netValue, 0);

        let customerType = 'Titular';
        const typeName = (item.user_type || '').toString().toLowerCase();
        if (typeName.includes('depend')) {
          customerType = 'Dependente';
        }

        return {
          code: item.code,
          name: item.name,
          customerType,
          station: item.station,
          service: item.subscription_description,
          grossValue,
          feeValue,
          netValue,
          paymentType: item.payment_type,
          subscriptionDate: item.subscription_date,
        };
      })
      .sort((a, b) => {
        const dateA = new Date(a.subscriptionDate || 0).getTime();
        const dateB = new Date(b.subscriptionDate || 0).getTime();
        return dateB - dateA;
      });
  }, [recurrenceFilteredData]);

  const recurrenceTableColumns = [
    { title: 'Cód. Fatura', field: 'code' },
    {
      title: 'Data Pagamento',
      field: 'subscriptionDate',
      render: (rowData) =>
        rowData.subscriptionDate
          ? moment(rowData.subscriptionDate).format('DD/MM/YYYY HH:mm')
          : '-',
    },
    { title: 'Cliente', field: 'name' },
    { title: 'Tipo', field: 'customerType' },
    { title: 'Entidade', field: 'station' },
    { title: 'Serviço', field: 'service' },
    {
      title: 'Valor',
      field: 'grossValue',
      render: (rowData) =>
        (rowData.grossValue || 0).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }),
    },
    {
      title: 'Taxa',
      field: 'feeValue',
      render: (rowData) =>
        (rowData.feeValue || 0).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }),
    },
    {
      title: 'Líquido',
      field: 'netValue',
      render: (rowData) =>
        (rowData.netValue || 0).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }),
    },
    {
      title: 'Forma de Pagamento',
      field: 'paymentType',
      render: (rowData) => PAYMENT_LABEL[rowData.paymentType] || rowData.paymentType,
    },
  ];

  const recurrenceTableCsvData = useMemo(() => {
    return recurrenceTableRows.map((row) => ({
      'Cód. Fatura': row.code || '-',
      'Data Pagamento': row.subscriptionDate
        ? moment(row.subscriptionDate).format('DD/MM/YYYY HH:mm')
        : '-',
      Cliente: row.name || '-',
      Tipo: row.customerType || '-',
      Entidade: row.station || '-',
      Serviço: row.service || '-',
      Valor: (row.grossValue || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }),
      Taxa: (row.feeValue || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }),
      Líquido: (row.netValue || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }),
      'Forma de Pagamento': PAYMENT_LABEL[row.paymentType] || row.paymentType,
    }));
  }, [recurrenceTableRows]);

  const principalMonthlyData = useMemo(() => {
    const grouped = {};

    principalFilteredSales.forEach((item) => {
      const date = moment(item.createdAt);
      if (!date.isValid()) return;

      const key = date.format('YYYY-MM');
      if (!grouped[key]) {
        grouped[key] = {
          date: date.format('MMM'),
          Vendas: 0,
        };
      }

      grouped[key].Vendas += 1;
    });

    return Object.keys(grouped)
      .sort()
      .map((key) => grouped[key]);
  }, [principalFilteredSales]);

  const principalEntityBarData = useMemo(() => {
    const counts = {};

    principalFilteredSales.forEach((item) => {
      const entityName = getEntityName(item);

      if (!entityName) return;
      counts[entityName] = (counts[entityName] || 0) + 1;
    });

    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [principalFilteredSales]);

  const salesTabFilteredOrders = useMemo(() => {
    return principalFilteredSales.filter((item) => {
      const isRecurringPaymentLink =
        item?.saleSource === 'payment_link' &&
        item?.recurrence_type &&
        item.recurrence_type !== 'single';
      const isSalesItem =
        !!item.product || (item?.saleSource === 'payment_link' && !isRecurringPaymentLink);

      if (!isSalesItem) {
        return false;
      }

      if (!voucherStatusFilter) return true;

      if (!isVoucherApplicable(item)) {
        return false;
      }

      const validatedStatus = isVoucherValidated(item.validated);
      const statusMatch =
        (voucherStatusFilter === 'resgatado' && validatedStatus) ||
        (voucherStatusFilter === 'aguardando' && !validatedStatus);

      return statusMatch;
    });
  }, [principalFilteredSales, voucherStatusFilter]);

  const salesTabSalesCount = salesTabFilteredOrders.length;

  const salesTabTotalSold = useMemo(() => {
    return salesTabFilteredOrders.reduce((sum, item) => {
      return sum + getSaleGrossValue(item);
    }, 0);
  }, [salesTabFilteredOrders]);

  const salesTabActivityBarData = useMemo(() => {
    const counts = {};

    salesTabFilteredOrders.forEach((item) => {
      const activityName = item?.action?.name || 'Outras atividades';
      if (!activityName) return;
      counts[activityName] = (counts[activityName] || 0) + 1;
    });

    return Object.entries(counts)
      .map(([name, value]) => ({
        name: formatActivityLabel(name),
        fullName: name,
        value,
      }))
      .sort((itemA, itemB) => itemB.value - itemA.value)
      .slice(0, ACTIVITY_CHART_LIMIT);
  }, [salesTabFilteredOrders]);

  const salesTabEntityBarData = useMemo(() => {
    const counts = {};

    salesTabFilteredOrders.forEach((item) => {
      const entityName = getEntityName(item);
      if (!entityName) return;
      counts[entityName] = (counts[entityName] || 0) + 1;
    });

    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [salesTabFilteredOrders]);

  const salesTabVoucherPieData = useMemo(() => {
    let aguardando = 0;
    let resgatado = 0;

    salesTabFilteredOrders.forEach((item) => {
      if (!isVoucherApplicable(item)) {
        return;
      }

      const isValidated = isVoucherValidated(item.validated);

      if (isValidated) {
        resgatado += 1;
      } else {
        aguardando += 1;
      }
    });

    return {
      data: [
        { name: 'Aguardando', value: aguardando },
        { name: 'Resgatado', value: resgatado },
      ],
      total: aguardando + resgatado,
    };
  }, [salesTabFilteredOrders]);

  const salesTabPaymentBarData = useMemo(() => {
    const counts = {};

    salesTabFilteredOrders.forEach((item) => {
      const method = item.gateway_charge?.payment_method;
      if (!method) return;

      const label = getPaymentMethodLabel(method);

      counts[label] = (counts[label] || 0) + 1;
    });

    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [salesTabFilteredOrders]);

  const salesTabTableRows = useMemo(() => {
    return salesTabFilteredOrders
      .map((item) => {
        const orderId = item.purchase_id || item.id || item.code || '-';
        const quantityItems = item.itemsCount || 1;
        const customerName = item.user?.name || '-';

        const isDependent =
          item.user?.is_dependent ||
          item.user?.dependent ||
          item.user?.isDependent ||
          item.user?.dependent_id ||
          item.user?.dependentName ||
          item.user?.type === 'dependent';

        const customerType = isDependent ? 'Dependente' : 'Titular';

        const entityName = getEntityName(item);
        const purchaseDate = item.createdAt
          ? moment(item.createdAt).format('DD/MM/YYYY HH:mm')
          : '-';

        const productName = item.product?.name || '-';

        const voucherStatus = getVoucherStatusLabel(item);
        const voucherCode = isVoucherApplicable(item) ? item.code || '-' : '-';

        return {
          orderId,
          quantityItems,
          customerName,
          customerType,
          entityName,
          purchaseDate,
          productName,
          voucherStatus,
          voucherCode,
          createdAtRaw: item.createdAt,
        };
      })
      .sort((a, b) => {
        const dateA = new Date(a.createdAtRaw || 0).getTime();
        const dateB = new Date(b.createdAtRaw || 0).getTime();
        return dateB - dateA;
      });
  }, [salesTabFilteredOrders]);

  const salesTabTableColumns = [
    { title: '#Pedido', field: 'orderId' },
    { title: 'Qtd. Itens', field: 'quantityItems' },
    { title: 'Cliente', field: 'customerName' },
    { title: 'Tipo', field: 'customerType' },
    { title: 'Entidade', field: 'entityName' },
    { title: 'Data da Compra', field: 'purchaseDate' },
    { title: 'Produto', field: 'productName' },
    { title: 'Sit. Voucher', field: 'voucherStatus' },
    { title: 'Voucher', field: 'voucherCode' },
  ];

  const salesTabTableCsvData = useMemo(() => {
    return salesTabTableRows.map((row) => ({
      '#Pedido': row.orderId,
      'Qtd. Itens': row.quantityItems,
      Cliente: row.customerName,
      Tipo: row.customerType,
      Entidade: row.entityName,
      'Data da Compra': row.purchaseDate,
      Produto: row.productName,
      'Sit. Voucher': row.voucherStatus,
      Voucher: row.voucherCode,
    }));
  }, [salesTabTableRows]);

  const principalVoucherPieData = useMemo(() => {
    let aguardando = 0;
    let resgatado = 0;

    principalFilteredSales.forEach((item) => {
      if (!isVoucherApplicable(item)) {
        return;
      }

      const isValidated = isVoucherValidated(item.validated);

      if (isValidated) {
        resgatado += 1;
      } else {
        aguardando += 1;
      }
    });

    return {
      data: [
        { name: 'Aguardando', value: aguardando },
        { name: 'Resgatado', value: resgatado },
      ],
      total: aguardando + resgatado,
    };
  }, [principalFilteredSales]);

  const principalPaymentBarData = useMemo(() => {
    const counts = {};

    principalFilteredSales.forEach((item) => {
      const method = item.gateway_charge?.payment_method;
      if (!method) return;

      const label = getPaymentMethodLabel(method);

      counts[label] = (counts[label] || 0) + 1;
    });

    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [principalFilteredSales]);

  const validatesVouchers = filteredSubscriptions.filter((voucher) => voucher.validated === '1');
  const validatesProducOrders = filteredProductOrders.filter((voucher) => voucher.validated);

  const totalValidSubs = validatesVouchers.length;
  const totalValidProd = validatesProducOrders.length;
  const qntdSubscriptionsRetirados = filteredSubscriptions.length;
  const qntdProductOrdersRetirados = filteredProductOrders.length;

  const totalProductOrders = useMemo(() => {
    const relevantOrders = isEmpty(selectedProducts)
      ? confirmedProductOrders
      : filteredProductOrders.filter((order) =>
          selectedProducts.some(
            (product) => product.id === order.product?.id && order?.status === PAGAMENTO_CONFIRMADO
          )
        );

    return relevantOrders.reduce((sum, order) => {
      const price = order?.product?.value - order?.product?.discount_value || 0;
      const value = parseFloat(price);
      return sum + value;
    }, 0);
  }, [filteredProductOrders, selectedProducts]);

  const conversaoData = useMemo(() => {
    const relevantOrders = isEmpty(selectedProducts)
      ? filteredProductOrders
      : filteredProductOrders.filter((order) =>
          selectedProducts.some((product) => product.id === order.product?.id)
        );

    const valorBruto = relevantOrders.reduce((sum, order) => {
      const price = parseFloat(order?.product?.value) || 0;
      return sum + price;
    }, 0);

    const valorMeta = relevantOrders.reduce((sum, order) => {
      const value = parseFloat(order?.product?.value) || 0;
      const discount = parseFloat(order?.product?.discount_value) || 0;
      const basePrice = value - discount;
      const price = basePrice - basePrice * PERCENTUAL;
      return sum + price;
    }, 0);

    const valorAtingido = relevantOrders
      .filter((order) => order.status === PAGAMENTO_CONFIRMADO)
      .reduce((sum, order) => {
        const value = parseFloat(order?.product?.value) || 0;
        const discount = parseFloat(order?.product?.discount_value) || 0;
        const basePrice = value - discount;
        const price = basePrice - basePrice * PERCENTUAL;
        return sum + price;
      }, 0);

    const restante = Math.max(valorMeta - valorAtingido, 0);

    return {
      data: [
        {
          name: 'Atingido',
          value: valorAtingido,
        },
        {
          name: 'Restante',
          value: restante,
        },
      ],
      meta: valorMeta,
      bruto: valorBruto,
    };
  }, [filteredProductOrders, selectedProducts]);

  const productsToChart = useMemo(() => {
    const uniqueProducts = filteredProductOrders.reduce((acc, order) => {
      const product = order.product;
      if (product && !acc.has(product.id)) {
        acc.set(product.id, { title: product.name, id: product.id });
      }
      return acc;
    }, new Map());
    return Array.from(uniqueProducts.values());
  }, [filteredProductOrders]);

  const actionsToChart = useMemo(() => {
    const uniqueActions = filteredSubscriptions.reduce((acc, sb) => {
      const action = sb.presential_course?.action;
      if (action && !acc.has(action.id)) {
        acc.set(action.id, {
          title: action.name,
          id: action.id,
          start: moment(action.start).format('DD/MM/YYYY'),
        });
      }
      return acc;
    }, new Map());
    return Array.from(uniqueActions.values());
  }, [filteredSubscriptions]);

  const resumoProdutos = confirmedProductOrders.reduce((acc, order) => {
    const name = order.product?.name;
    const validated = order.validated;

    if (!name) return acc;

    if (!acc[name]) {
      acc[name] = {
        name,
        retirados: 0,
        validados: 0,
      };
    }

    acc[name].retirados += 1;
    if (validated) {
      acc[name].validados += 1;
    }

    return acc;
  }, {});

  const productsBarData = Object.values(resumoProdutos);

  const filteredBarData = useMemo(() => {
    return productsBarData.filter((data) =>
      selectedProducts.some((product) => product.title === data.name)
    );
  }, [selectedProducts]);

  const resumoActions = filteredSubscriptions.reduce((acc, sb) => {
    const name = sb.presential_course?.action?.name;
    const validated = sb.validated === '1';

    if (!name) return acc;

    if (!acc[name]) {
      acc[name] = {
        name,
        retirados: 0,
        validados: 0,
      };
    }
    acc[name].retirados += 1;
    if (validated) {
      acc[name].validados += 1;
    }
    return acc;
  }, {});
  const actionsBarData = Object.values(resumoActions);
  const filteredActionsBarData = useMemo(() => {
    return actionsBarData.filter((data) =>
      selectedProducts.some((action) => action.title === data.name)
    );
  }, [selectedProducts]);

  const transformGroupedToChartFormat = (groupedData) =>
    Object.entries(groupedData).map(([date, keys]) => ({ date, ...keys }));

  const filteredLineData = useMemo(() => {
    const grouped = groupByDateAndKey(
      confirmedProductOrders,
      (order) => order.product?.name,
      (order) => order.createdAt,
      (order) => selectedProducts.some((p) => p.id === order.product?.id)
    );

    return transformGroupedToChartFormat(grouped).sort(
      (a, b) => moment(a.date, 'DD/MM/YYYY').toDate() - moment(b.date, 'DD/MM/YYYY').toDate()
    );
  }, [confirmedProductOrders, selectedProducts]);

  const filteredActivityLineData = useMemo(() => {
    const grouped = groupByDateAndKey(
      filteredSubscriptions,
      (sub) => sub.presential_course?.action?.name,
      (sub) => sub.createdAt,
      (sub) => selectedProducts.some((a) => a.id === sub.presential_course?.action?.id)
    );

    return transformGroupedToChartFormat(grouped).sort(
      (a, b) => moment(a.date, 'DD/MM/YYYY').toDate() - moment(b.date, 'DD/MM/YYYY').toDate()
    );
  }, [filteredSubscriptions, selectedProducts]);

  const stockData = useMemo(() => {
    const selectedActivity = selectedProducts.length === 1 ? selectedProducts[0] : null;

    const relevantActivities = selectedActivity
      ? confirmedProductOrders.filter(
          (subscription) => subscription.presential_course?.action?.id === selectedActivity.id
        )
      : [];

    const groupedData = relevantActivities.reduce((acc, subscription) => {
      const action = subscription.presential_course?.action;
      if (!action) return acc;

      const { id, limit, name } = action;

      if (!acc[id]) {
        acc[id] = {
          name,
          limit: limit || 0,
          retiradas: 0,
        };
      }

      acc[id].retiradas += 1;
      return acc;
    }, {});

    const totalLimit = Object.values(groupedData).reduce(
      (sum, activity) => sum + activity.limit,
      0
    );
    const totalRetiradas = Object.values(groupedData).reduce(
      (sum, activity) => sum + activity.retiradas,
      0
    );

    return {
      data: [
        { name: 'Retiradas', value: totalRetiradas },
        { name: 'Disponível', value: Math.max(totalLimit - totalRetiradas, 0) },
      ],
      total: totalLimit,
    };
  }, [confirmedProductOrders, selectedProducts]);

  const productStockData = useMemo(() => {
    const selectedProduct = selectedProducts.length === 1 ? selectedProducts[0] : null;

    const relevantOrders = selectedProduct
      ? confirmedProductOrders.filter((order) => order.product?.id === selectedProduct.id)
      : [];

    const groupedData = relevantOrders.reduce((acc, order) => {
      const product = order.product;
      const activity = order?.action;
      if (!product || !activity) return acc;

      const { id, stock, name } = product;

      if (!acc[id]) {
        acc[id] = {
          name,
          stock: stock || 0,
          retiradas: 0,
          activities: new Set(),
        };
      }

      acc[id].retiradas += 1;
      acc[id].activities.add(activity.name);
      return acc;
    }, {});

    const totalStock = Object.values(groupedData).reduce((sum, product) => sum + product.stock, 0);
    const totalRetiradas = Object.values(groupedData).reduce(
      (sum, product) => sum + product.retiradas,
      0
    );

    const activities = Object.values(groupedData).flatMap((product) =>
      Array.from(product.activities)
    );

    return {
      data: [
        { name: 'Retiradas', value: totalRetiradas },
        { name: 'Disponível', value: Math.max(totalStock - totalRetiradas, 0) },
      ],
      total: totalStock,
      activities,
    };
  }, [confirmedProductOrders, selectedProducts]);

  const getColor = (index) => {
    const colors = ['#fd7426', '#02a995', '#8884d8', '#ebd539', '#41b8d5', '#6b349c'];
    return colors[index % colors.length];
  };

  useEffect(() => {
    if (!station) return;
    getAllDashboardData(station);
  }, [station, getAllDashboardData]);

  // Render progressivo: quando loading termina, libera os tabs primeiro,
  // depois renderiza o conteúdo pesado (gráficos/tabelas) no próximo frame
  useEffect(() => {
    if (prevLoadingRef.current && !loading) {
      setDataReady(false);
      requestAnimationFrame(() => {
        setDataReady(true);
      });
    }
    prevLoadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    if (activeTab === 'recorrencia' && station && !recurrenceLoaded) {
      getUsersSubscriptionActionsList(station);
      setRecurrenceLoaded(true);
    }
  }, [activeTab, station, recurrenceLoaded, getUsersSubscriptionActionsList]);

  useEffect(() => {
    const filtros = {
      startDate,
      endDate,
      selectedProducts,
    };
    localStorage.setItem('filtrosDashboard', JSON.stringify(filtros));
  }, [startDate, endDate, selectedProducts]);

  const logoMap = {
    1: logo_sindilegis,
    148: logo_astcu,
    178: logo_sindjus,
  };

  const logo = logoMap[station] || logo_clubs;

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedProducts([]);
  };

  const summaryCards = [
    {
      imageSrc: total_sales,
      title: 'Total vendido',
      value: totalProductOrders,
      valueFormatter: (value) =>
        value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    },
    {
      imageSrc: download,
      title: 'Voucher’s retirados',
      value: qntdProductOrdersRetirados,
      valueFormatter: (value) => value.toLocaleString('pt-BR'),
    },
    {
      imageSrc: validator,
      title: 'Voucher’s validados',
      value: totalValidProd,
      valueFormatter: (value) => value.toLocaleString('pt-BR'),
    },
  ];

  return (
    <Box sx={{ flexGrow: 1, p: 3, minHeight: 'auto' }}>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '80vh',
          }}>
          <LoadingProgress />
          <Typography variant="subtitle1" style={{ color: 'black', marginLeft: '10px' }}>
            {' '}
            Aguarde, estamos buscando os dados...
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              backgroundColor: 'white',
              borderRadius: '8px 8px 0 0',
              padding: '4px 16px',
              marginBottom: 2,
            }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary">
              <Tab label="Principal" value="principal" />
              <Tab label="Vendas" value="vendas" />
              <Tab label="Recorrência" value="recorrencia" />
            </Tabs>
          </Box>

          {!dataReady && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 200,
              }}>
              <LoadingProgress />
              <Typography variant="subtitle1" style={{ color: 'black', marginLeft: '10px' }}>
                Carregando dados...
              </Typography>
            </Box>
          )}

          {dataReady && activeTab === 'principal' && (
            <Box sx={styles.topCardsScroller}>
              <Grid container spacing={2} wrap="nowrap" style={styles.topCardsGrid}>
                <Grid item style={LOGO_CARD_ITEM_STYLE}>
                  <Box sx={styles.topLogoInline}>
                    <img src={logo} style={{ height: 80, width: 'auto' }} />
                  </Box>
                </Grid>
                <Grid item style={FILTER_CARD_ITEM_STYLE}>
                  <Card style={styles.cardPeriodo}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle2" noWrap>
                        Ano
                      </Typography>
                      <select
                        style={{ width: '100%', marginTop: 8 }}
                        value={yearFilter}
                        onChange={(e) => setYearFilter(e.target.value)}>
                        <option value="">Todos</option>
                        {availableYears.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </Box>
                  </Card>
                </Grid>
                <Grid item style={FILTER_CARD_ITEM_STYLE}>
                  <Card style={styles.cardPeriodo}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle2" noWrap>
                        Mês
                      </Typography>
                      <select
                        style={{ width: '100%', marginTop: 8 }}
                        value={monthFilter}
                        onChange={(e) => setMonthFilter(e.target.value)}>
                        <option value="">Todos</option>
                        {availableMonths.map((month) => (
                          <option key={month} value={month}>
                            {moment()
                              .month(month - 1)
                              .format('MMM')}
                          </option>
                        ))}
                      </select>
                    </Box>
                  </Card>
                </Grid>
                <Grid item style={FILTER_CARD_ITEM_STYLE}>
                  <Card style={styles.cardPeriodo}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle2" noWrap>
                        Parceiros
                      </Typography>
                      <select
                        style={{ width: '100%', marginTop: 8 }}
                        value={partnerFilter}
                        onChange={(e) => setPartnerFilter(e.target.value)}>
                        <option value="">Todos</option>
                        {availablePartners.map((partner) => (
                          <option key={partner} value={partner}>
                            {partner}
                          </option>
                        ))}
                      </select>
                    </Box>
                  </Card>
                </Grid>
                <Grid item style={SUMMARY_CARD_ITEM_STYLE}>
                  <SummaryCard
                    imageSrc={null}
                    title="Qnt. Vendas"
                    value={principalSalesCount}
                    valueFormatter={(value) => value.toLocaleString('pt-BR')}
                    cardStyle={styles.cardResumo}
                  />
                </Grid>
                <Grid item style={VALUE_CARD_ITEM_STYLE}>
                  <SummaryCard
                    imageSrc={null}
                    title="Valor vendido"
                    value={principalTotalSold}
                    valueFormatter={(value) =>
                      value.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })
                    }
                    cardStyle={styles.cardResumo}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {dataReady && activeTab === 'principal' && (
            <Grid container spacing={2} style={{ marginTop: 20 }}>
              <Grid item xs={12}>
                <Card style={{ ...styles.paperGraficoGrande, maxWidth: '100%' }}>
                  <Box sx={{ ...styles.boxTitulo }}>
                    <Typography variant="subtitle1">Vendas por mês</Typography>
                  </Box>
                  {isEmpty(principalMonthlyData) ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 180,
                      }}>
                      <Typography variant="subtitle1" align="center">
                        Sem dados para o período selecionado
                      </Typography>
                    </Box>
                  ) : (
                    <ReLineChart
                      width={LARGE_LINE_CHART_WIDTH}
                      height={LARGE_LINE_CHART_HEIGHT}
                      data={principalMonthlyData}
                      margin={{ top: 10, right: 40, left: 10, bottom: 25 }}>
                      <ReCartesianGrid strokeDasharray="3 3" />
                      <ReXAxis dataKey="date" />
                      <ReYAxis allowDecimals={false} />
                      <ReTooltip />
                      <ReLegend verticalAlign="bottom" height={36} />
                      <ReLine type="monotone" dataKey="Vendas" stroke="#6b349c" />
                    </ReLineChart>
                  )}
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card style={styles.paperGrafico}>
                  <Box sx={{ ...styles.boxTitulo }}>
                    <Typography variant="subtitle1">Vendas por entidade</Typography>
                  </Box>
                  {isEmpty(principalEntityBarData) ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 160,
                      }}>
                      <Typography variant="subtitle1" align="center">
                        Sem dados
                      </Typography>
                    </Box>
                  ) : (
                    <ReBarChart
                      width={SMALL_CHART_WIDTH}
                      height={SMALL_CHART_HEIGHT}
                      data={principalEntityBarData}
                      margin={{ top: 10, right: 20, left: 10, bottom: 35 }}>
                      <ReCartesianGrid strokeDasharray="3 3" />
                      <ReXAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
                      <ReYAxis allowDecimals={false} />
                      <ReTooltip />
                      <ReBar dataKey="value" fill="#6b349c" radius={[10, 10, 0, 0]} />
                    </ReBarChart>
                  )}
                </Card>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Card style={styles.paperGrafico}>
                  <Box sx={{ ...styles.boxTitulo }}>
                    <Typography variant="subtitle1">Situação do Voucher</Typography>
                  </Box>
                  {principalVoucherPieData.total === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 160,
                      }}>
                      <Typography variant="subtitle1" align="center">
                        Sem dados
                      </Typography>
                    </Box>
                  ) : (
                    <RePieChart width={SMALL_CHART_WIDTH} height={SMALL_CHART_HEIGHT}>
                      <RePie
                        data={principalVoucherPieData.data}
                        cx="50%"
                        cy="50%"
                        outerRadius={60}
                        dataKey="value">
                        {principalVoucherPieData.data.map((entry, index) => (
                          <ReCell
                            key={`cell-${index}`}
                            fill={index === 0 ? '#cbb2f5' : '#6b349c'}
                          />
                        ))}
                      </RePie>
                      <ReTooltip />
                      <ReLegend verticalAlign="bottom" height={36} />
                    </RePieChart>
                  )}
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card style={styles.paperGrafico}>
                  <Box sx={{ ...styles.boxTitulo }}>
                    <Typography variant="subtitle1">Forma de pagamento</Typography>
                  </Box>
                  {isEmpty(principalPaymentBarData) ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 160,
                      }}>
                      <Typography variant="subtitle1" align="center">
                        Sem dados
                      </Typography>
                    </Box>
                  ) : (
                    <ReBarChart
                      width={SMALL_CHART_WIDTH}
                      height={SMALL_CHART_HEIGHT}
                      data={principalPaymentBarData}
                      margin={{ top: 10, right: 20, left: 10, bottom: 35 }}>
                      <ReCartesianGrid strokeDasharray="3 3" />
                      <ReXAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
                      <ReYAxis allowDecimals={false} />
                      <ReTooltip />
                      <ReBar dataKey="value" fill="#6b349c" radius={[10, 10, 0, 0]} />
                    </ReBarChart>
                  )}
                </Card>
              </Grid>
            </Grid>
          )}

          {dataReady && activeTab === 'vendas' && (
            <Box sx={styles.topCardsScroller}>
              <Grid container spacing={2} wrap="nowrap" style={styles.topCardsGridWide}>
                <Grid item style={LOGO_CARD_ITEM_STYLE}>
                  <Box sx={styles.topLogoInline}>
                    <img src={logo} style={{ height: 80, width: 'auto' }} />
                  </Box>
                </Grid>
                <Grid item style={FILTER_CARD_ITEM_STYLE}>
                  <Card style={styles.cardPeriodo}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle2" noWrap>
                        Ano
                      </Typography>
                      <select
                        style={{ width: '100%', marginTop: 8 }}
                        value={yearFilter}
                        onChange={(e) => setYearFilter(e.target.value)}>
                        <option value="">Todos</option>
                        {availableYears.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </Box>
                  </Card>
                </Grid>
                <Grid item style={FILTER_CARD_ITEM_STYLE}>
                  <Card style={styles.cardPeriodo}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle2" noWrap>
                        Mês
                      </Typography>
                      <select
                        style={{ width: '100%', marginTop: 8 }}
                        value={monthFilter}
                        onChange={(e) => setMonthFilter(e.target.value)}>
                        <option value="">Todos</option>
                        {availableMonths.map((month) => (
                          <option key={month} value={month}>
                            {moment()
                              .month(month - 1)
                              .format('MMM')}
                          </option>
                        ))}
                      </select>
                    </Box>
                  </Card>
                </Grid>
                <Grid item style={FILTER_CARD_ITEM_STYLE}>
                  <Card style={styles.cardPeriodo}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle2" noWrap>
                        Parceiros
                      </Typography>
                      <select
                        style={{ width: '100%', marginTop: 8 }}
                        value={partnerFilter}
                        onChange={(e) => setPartnerFilter(e.target.value)}>
                        <option value="">Todos</option>
                        {availablePartners.map((partner) => (
                          <option key={partner} value={partner}>
                            {partner}
                          </option>
                        ))}
                      </select>
                    </Box>
                  </Card>
                </Grid>
                <Grid item style={FILTER_CARD_ITEM_STYLE}>
                  <Card style={styles.cardPeriodo}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle2" noWrap>
                        Sit. Voucher
                      </Typography>
                      <select
                        style={{ width: '100%', marginTop: 8 }}
                        value={voucherStatusFilter}
                        onChange={(e) => setVoucherStatusFilter(e.target.value)}>
                        <option value="">Todos</option>
                        <option value="aguardando">Aguardando</option>
                        <option value="resgatado">Resgatado</option>
                      </select>
                    </Box>
                  </Card>
                </Grid>
                <Grid item style={SUMMARY_CARD_ITEM_STYLE}>
                  <SummaryCard
                    imageSrc={null}
                    title="Qnt. Vendas"
                    value={salesTabSalesCount}
                    valueFormatter={(value) => value.toLocaleString('pt-BR')}
                    cardStyle={styles.cardResumo}
                  />
                </Grid>
                <Grid item style={VALUE_CARD_ITEM_STYLE}>
                  <SummaryCard
                    imageSrc={null}
                    title="Valor vendido"
                    value={salesTabTotalSold}
                    valueFormatter={(value) =>
                      value.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })
                    }
                    cardStyle={styles.cardResumo}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {dataReady && activeTab === 'recorrencia' && (
            <Box sx={styles.topCardsScroller}>
              <Grid container spacing={2} wrap="nowrap" style={styles.topCardsGrid}>
                <Grid item style={LOGO_CARD_ITEM_STYLE}>
                  <Box sx={styles.topLogoInline}>
                    <img src={logo} style={{ height: 80, width: 'auto' }} />
                  </Box>
                </Grid>
                <Grid item style={FILTER_CARD_ITEM_STYLE}>
                  <Card style={styles.cardPeriodo}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle2" noWrap>
                        Ano
                      </Typography>
                      <select
                        style={{ width: '100%', marginTop: 8 }}
                        value={recYearFilter}
                        onChange={(e) => setRecYearFilter(e.target.value)}>
                        <option value="">Todos</option>
                        {recurrenceAvailableYears.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </Box>
                  </Card>
                </Grid>
                <Grid item style={FILTER_CARD_ITEM_STYLE}>
                  <Card style={styles.cardPeriodo}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle2" noWrap>
                        Mês
                      </Typography>
                      <select
                        style={{ width: '100%', marginTop: 8 }}
                        value={recMonthFilter}
                        onChange={(e) => setRecMonthFilter(e.target.value)}>
                        <option value="">Todos</option>
                        {recurrenceAvailableMonths.map((month) => (
                          <option key={month} value={month}>
                            {moment()
                              .month(month - 1)
                              .format('MMM')}
                          </option>
                        ))}
                      </select>
                    </Box>
                  </Card>
                </Grid>
                <Grid item style={FILTER_CARD_ITEM_STYLE}>
                  <Card style={styles.cardPeriodo}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle2" noWrap>
                        Parceiros
                      </Typography>
                      <select
                        style={{ width: '100%', marginTop: 8 }}
                        value={recPartnerFilter}
                        onChange={(e) => setRecPartnerFilter(e.target.value)}>
                        <option value="">Todos</option>
                        {recurrenceAvailablePartners.map((partner) => (
                          <option key={partner} value={partner}>
                            {partner}
                          </option>
                        ))}
                      </select>
                    </Box>
                  </Card>
                </Grid>
                <Grid item style={SUMMARY_CARD_ITEM_STYLE}>
                  <SummaryCard
                    imageSrc={null}
                    title="Qnt. Vendas"
                    value={recurrenceSalesCount}
                    valueFormatter={(value) => value.toLocaleString('pt-BR')}
                    cardStyle={styles.cardResumo}
                  />
                </Grid>
                <Grid item style={VALUE_CARD_ITEM_STYLE}>
                  <SummaryCard
                    imageSrc={null}
                    title="Valor vendido"
                    value={recurrenceTotalSold}
                    valueFormatter={(value) =>
                      value.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })
                    }
                    cardStyle={styles.cardResumo}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {dataReady && activeTab === 'vendas' && (
            <Grid container spacing={2} style={{ marginTop: 20 }}>
              <Grid item xs={12} md={7}>
                <Card style={{ ...styles.paperGrafico, minHeight: 380 }}>
                  <Box sx={{ ...styles.boxTitulo }}>
                    <Typography variant="subtitle1">Vendas por atividade</Typography>
                  </Box>
                  {isEmpty(salesTabActivityBarData) ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 160,
                      }}>
                      <Typography variant="subtitle1" align="center">
                        Sem dados
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={styles.chartAreaLarge}>
                      <ReResponsiveContainer width="100%" height="100%">
                        <ReBarChart
                          layout="vertical"
                          data={salesTabActivityBarData}
                          margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
                          <ReCartesianGrid strokeDasharray="3 3" />
                          <ReXAxis type="number" allowDecimals={false} />
                          <ReYAxis
                            type="category"
                            dataKey="name"
                            tick={{ fontSize: 11 }}
                            width={160}
                          />
                          <ReTooltip
                            labelFormatter={(_label, payload) =>
                              payload?.[0]?.payload?.fullName || _label
                            }
                          />
                          <ReBar
                            dataKey="value"
                            fill="#6b349c"
                            radius={[0, 10, 10, 0]}
                            barSize={20}
                          />
                        </ReBarChart>
                      </ReResponsiveContainer>
                    </Box>
                  )}
                </Card>
              </Grid>

              <Grid item xs={12} md={5}>
                <Card style={styles.paperGrafico}>
                  <Box sx={{ ...styles.boxTitulo }}>
                    <Typography variant="subtitle1">Vendas por entidade</Typography>
                  </Box>
                  {isEmpty(salesTabEntityBarData) ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 160,
                      }}>
                      <Typography variant="subtitle1" align="center">
                        Sem dados
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={styles.chartArea}>
                      <ReResponsiveContainer width="100%" height="100%">
                        <ReBarChart
                          data={salesTabEntityBarData}
                          margin={{ top: 10, right: 20, left: 10, bottom: 35 }}>
                          <ReCartesianGrid strokeDasharray="3 3" />
                          <ReXAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
                          <ReYAxis allowDecimals={false} />
                          <ReTooltip />
                          <ReBar dataKey="value" fill="#6b349c" radius={[10, 10, 0, 0]} />
                        </ReBarChart>
                      </ReResponsiveContainer>
                    </Box>
                  )}
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card style={styles.paperGrafico}>
                  <Box sx={{ ...styles.boxTitulo }}>
                    <Typography variant="subtitle1">Situação do Voucher</Typography>
                  </Box>
                  {salesTabVoucherPieData.total === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 160,
                      }}>
                      <Typography variant="subtitle1" align="center">
                        Sem dados
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={styles.chartArea}>
                      <ReResponsiveContainer width="100%" height="100%">
                        <RePieChart>
                          <RePie
                            data={salesTabVoucherPieData.data}
                            cx="50%"
                            cy="45%"
                            outerRadius={72}
                            dataKey="value">
                            {salesTabVoucherPieData.data.map((entry, index) => (
                              <ReCell
                                key={`cell-sales-voucher-${index}`}
                                fill={index === 0 ? '#cbb2f5' : '#6b349c'}
                              />
                            ))}
                          </RePie>
                          <ReTooltip />
                          <ReLegend verticalAlign="bottom" height={36} />
                        </RePieChart>
                      </ReResponsiveContainer>
                    </Box>
                  )}
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card style={styles.paperGrafico}>
                  <Box sx={{ ...styles.boxTitulo }}>
                    <Typography variant="subtitle1">Forma de pagamento</Typography>
                  </Box>
                  {isEmpty(salesTabPaymentBarData) ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 160,
                      }}>
                      <Typography variant="subtitle1" align="center">
                        Sem dados
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={styles.chartArea}>
                      <ReResponsiveContainer width="100%" height="100%">
                        <ReBarChart
                          data={salesTabPaymentBarData}
                          margin={{ top: 10, right: 20, left: 10, bottom: 35 }}>
                          <ReCartesianGrid strokeDasharray="3 3" />
                          <ReXAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
                          <ReYAxis allowDecimals={false} />
                          <ReTooltip />
                          <ReBar dataKey="value" fill="#6b349c" radius={[10, 10, 0, 0]} />
                        </ReBarChart>
                      </ReResponsiveContainer>
                    </Box>
                  )}
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card style={{ ...styles.paperGraficoGrande, maxWidth: '100%', height: 'auto' }}>
                  <Box sx={{ ...styles.boxTitulo }}>
                    <Typography variant="subtitle1">Pedidos</Typography>
                  </Box>
                  <TableContainer style={{ maxHeight: 500 }}>
                    <Table
                      hasToolbar
                      defaultSelected={[]}
                      columns={salesTabTableColumns}
                      rows={salesTabTableRows}
                      onChangeOption={() => {}}
                      mobileTable={isMobile}
                      hasBorder={!isMobile}
                      hideLinesPerPage
                      hideRowPerPageOptions
                      buttonCSV
                      csvData={salesTabTableCsvData}
                    />
                  </TableContainer>
                </Card>
              </Grid>
            </Grid>
          )}

          {dataReady && activeTab === 'recorrencia' && (
            <Grid container spacing={2} style={{ marginTop: 20 }}>
              <Grid item xs={12} sm={6}>
                <Card style={styles.paperGrafico}>
                  <Box sx={{ ...styles.boxTitulo }}>
                    <Typography variant="subtitle1">Vendas por atividade</Typography>
                  </Box>
                  {isEmpty(recurrenceServiceBarData) ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 160,
                      }}>
                      <Typography variant="subtitle1" align="center">
                        Sem dados
                      </Typography>
                    </Box>
                  ) : (
                    <ReBarChart
                      layout="vertical"
                      width={SMALL_CHART_WIDTH}
                      height={SMALL_CHART_HEIGHT}
                      data={recurrenceServiceBarData}
                      margin={{ top: 10, right: 20, left: 40, bottom: 20 }}>
                      <ReCartesianGrid strokeDasharray="3 3" />
                      <ReXAxis type="number" allowDecimals={false} />
                      <ReYAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                      <ReTooltip />
                      <ReBar dataKey="value" fill="#6b349c" radius={[0, 10, 10, 0]} />
                    </ReBarChart>
                  )}
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card style={styles.paperGrafico}>
                  <Box sx={{ ...styles.boxTitulo }}>
                    <Typography variant="subtitle1">Vendas por entidade</Typography>
                  </Box>
                  {isEmpty(recurrenceEntityBarData) ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 160,
                      }}>
                      <Typography variant="subtitle1" align="center">
                        Sem dados
                      </Typography>
                    </Box>
                  ) : (
                    <ReBarChart
                      layout="vertical"
                      width={SMALL_CHART_WIDTH}
                      height={SMALL_CHART_HEIGHT}
                      data={recurrenceEntityBarData}
                      margin={{ top: 10, right: 20, left: 40, bottom: 20 }}>
                      <ReCartesianGrid strokeDasharray="3 3" />
                      <ReXAxis type="number" allowDecimals={false} />
                      <ReYAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                      <ReTooltip />
                      <ReBar dataKey="value" fill="#6b349c" radius={[0, 10, 10, 0]} />
                    </ReBarChart>
                  )}
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card style={{ ...styles.paperGraficoGrande, maxWidth: '100%', height: 'auto' }}>
                  <Box sx={{ ...styles.boxTitulo }}>
                    <Typography variant="subtitle1">Pedidos</Typography>
                  </Box>
                  <TableContainer style={{ maxHeight: 500 }}>
                    <Table
                      hasToolbar
                      defaultSelected={[]}
                      columns={recurrenceTableColumns}
                      rows={recurrenceTableRows}
                      onChangeOption={() => {}}
                      mobileTable={isMobile}
                      hasBorder={!isMobile}
                      hideLinesPerPage
                      hideRowPerPageOptions
                      buttonCSV
                      csvData={recurrenceTableCsvData}
                    />
                  </TableContainer>
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};

const mapStatToProps = (state) => {
  return {
    vouchers: state.financialDashboard.list,
    auth: state.auth,
    productOrders: state.financialDashboard.listProductsOrders,
    loading: state.financialDashboard.loading,
    usersSubscriptions: state.subscribeProduct.listUsersSubscriptions,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      getListDashboard,
      getListProductsOrders,
      getAllDashboardData,
      getUsersSubscriptionActionsList,
    },
    dispatch
  );
};

export default connect(mapStatToProps, mapDispatchToProps)(Dashboard);
